import BeenhereIcon from '@mui/icons-material/Beenhere'
import ContactMailIcon from '@mui/icons-material/ContactMail'
import DesignServicesIcon from '@mui/icons-material/DesignServices'
import FolderIcon from '@mui/icons-material/Folder'
import PolylineIcon from '@mui/icons-material/Polyline'
import SettingsInputCompositeIcon from '@mui/icons-material/SettingsInputComposite'
import Badge from '@mui/material/Badge'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import type { DisciplineVerzameling } from '@models/DisciplineVerzameling'
import { formatDate } from '@util/date-util'

import type { AssignedBijlage } from '../../../entity/bijlagen/types/AssignedBijlage'
import type { Mijlpaal } from '../types/Mijlpalen'

import { colorStyles } from './MijlpaalColorPicker'

const icon = {
  m: '1px',
  borderRadius: '100%',
  backgroundPosition: 'center 5px',
  backgroundSize: '20px',
  backgroundRepeat: 'no-repeat',
  cursor: 'help',
  padding: '5px',
}

const badgeStyle = (color) => ({
  '& .MuiBadge-badge': { background: color, color: 'white' },
})

const BIJLAGEN = [
  'Schetsontwerp',
  'CROW 400 bodem',
  'Definitief ontwerp netbeheerder',
  'Vergunning',
  'Werkmap',
]
const bijlageToIconMap = {
  Werkmap: <FolderIcon sx={icon} />,
  'Definitief ontwerp netbeheerder': <DesignServicesIcon sx={icon} />,
  'CROW 400 bodem': <SettingsInputCompositeIcon sx={icon} />,
  Schetsontwerp: <PolylineIcon sx={icon} />,
  Vergunning: <ContactMailIcon sx={icon} />,
}

const TECHNISCH_GEREED_TYPE = 'technischGereed'

interface Props {
  bijlagen?: AssignedBijlage[]
  disciplineVerzamelingen: DisciplineVerzameling[]
  mijlpalen?: Mijlpaal[]
}

export const Mijlpalen = ({
  bijlagen = [],
  disciplineVerzamelingen,
  mijlpalen = [],
}: Props) => {
  const findMijlpaal = (type: string) => mijlpalen.find((m) => m.type === type)

  const getBadgeNumber = (type: string) => findMijlpaal(type)?.count || '0'

  const getBadgeColor = (type: string) =>
    colorStyles[findMijlpaal(type)?.color || 'grey']

  const tooltipBuilder = (documentsoort: string) =>
    bijlagen
      .filter((x) =>
        ['uploaded', 'assigned', 'review', 'done'].includes(x.status),
      )
      .filter((x) => x.documentsoort === documentsoort)
      .map((x) => (
        <Typography variant="body2" key={`${x.assignedTo}-${x.discipline}`}>
          {x.assignedTo} {x.discipline && `(${x.discipline})`} -{' '}
          {x.status === 'done'
            ? `Opgeleverd op ${formatDate(x.completed?.date)}`
            : `${x.dueWeek || 'Geen opleverweek'}`}
        </Typography>
      ))

  const tgTooltipBuilder = (
    disciplineVerzamelingen: DisciplineVerzameling[],
  ) => (
    <Typography variant="body2">
      {disciplineVerzamelingen.map((dv) => (
        <Typography key={`${dv.netbeheerderLabel}-${dv.disciplineCode}`}>
          {dv.netbeheerderLabel} ({dv.disciplineLabel}) -{' '}
          {dv.technischGereed
            ? `${formatDate(dv.technischGereed)}`
            : 'Niet technisch gereed'}
        </Typography>
      ))}
    </Typography>
  )

  return (
    <>
      {BIJLAGEN.map((b) => (
        <Tooltip
          key={b}
          title={
            <>
              <Typography>{b}</Typography>
              {tooltipBuilder(b)}
            </>
          }
        >
          <Badge
            badgeContent={getBadgeNumber(b)}
            overlap="circular"
            sx={{
              ...badgeStyle(getBadgeColor(b)),
            }}
          >
            {bijlageToIconMap[b]}
          </Badge>
        </Tooltip>
      ))}
      <Tooltip
        title={
          <>
            <Typography>Technisch gereed</Typography>
            {tgTooltipBuilder(disciplineVerzamelingen)}
          </>
        }
      >
        <Badge
          badgeContent={getBadgeNumber(TECHNISCH_GEREED_TYPE)}
          overlap="circular"
          sx={{
            ...badgeStyle(getBadgeColor(TECHNISCH_GEREED_TYPE)),
          }}
        >
          <BeenhereIcon sx={icon} />
        </Badge>
      </Tooltip>
    </>
  )
}
